import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { FAB } from "react-native-paper";
import {
  CompanyHeaders,
  CompanyMediumDeviceHeaders,
  CompanySmallDeviceHeaders,
} from "../components/headers/AdminOrganizations/headers";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { SalesOperationBadgeHorizontalList } from "../../../components/custom/Badge/SalesOperationBadgeHorizontalList";
import Spinner from "../../../components/shared/Spinner";
import { NestedDataTable } from "../../../components/custom/DataTable/NestedDataTableWithPagination2";
import {
  getOrganizations,
  searchOrganizations,
} from "../../../redux/dispatcher/adminDispatcher";
import { SearchAdminModulesWithSearchBar } from "../../../components/custom/Banners/SearchAdminModulesWithSearchBar";

export default function AdminOrganizations({ navigation }: any) {
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const { loadingOrganizations, organizations } = useSelector(
    (state: any) => state.admin
  );
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      CompanyHeaders
    );
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const operationsFilterCategoryList = [
    "All",
    "On-Track",
    "At-Risk",
    "Delayed",
    "Completed",
  ];

  const dispatch = useDispatch<any>();

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const navigateToOrder = (companyId: any, mode: string) => {
    navigation.navigate("AdminOrganizationalNavigate", {
      mode: mode,
      companyId: companyId,
    });
  };

  const fetchOrganizations = () => {
    dispatch(getOrganizations());
  };

  useEffect(() => {
    setRefresh(false);
    setTableHeaders(
      isLargeDevice
        ? CompanyHeaders
        : isMediumDevice
        ? CompanyMediumDeviceHeaders
        : CompanySmallDeviceHeaders
    );
    fetchOrganizations();
  }, [isFocused, isRefresh]);

  const renderFilters = () => (
    <View
      style={[styles.renderFilterStyles, !isLargeDevice && styles.mobileLayout]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        {!isSmallDevice && !isMobileDevice && (
          <Text style={styles.quickFilterText}>Quick Filter</Text>
        )}
        <SalesOperationBadgeHorizontalList
          itemsList={operationsFilterCategoryList}
          filterCategory={selectedCategory || ""}
          setFilterCategory={setSelectedCategory!}
          tableHeaders={tableHeaders}
        />
      </ScrollView>

      <SearchAdminModulesWithSearchBar
        titleName={`Total Items: ${organizations.length}`}
        setRefresh={setRefresh}
        dataToExport={organizations}
        fileName="organizationScreen"
        sheetName="Organizations Screen"
        disableTitle={true}
        noBorder={true}
        searchAPI={searchOrganizations}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <TouchableOpacity style={styles.addButton}>
          <Text
            style={styles.buttonText}
            onPress={() =>
              navigation.navigate("AdminHome", {
                screen: "AddAdminOrganizations",
              })
            }
          >
            Add Organization
          </Text>
        </TouchableOpacity>
      </View>
      <View style={styles.filterMainContainer}>{renderFilters()}</View>
      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loadingOrganizations ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <FAB
              icon="arrow-up"
              style={[
                styles.fab,
                {
                  top:
                    contentOffSet +
                    Dimensions.get("window").height -
                    fabPosition,
                },
              ]}
              onPress={() =>
                listRef.current?.scrollTo({ x: 0, y: 0, animated: true })
              }
              small
            />
            <NestedDataTable
              itemsPerPage={15}
              isNestedDataLoaded={false}
              nestedHeader={tableHeaders}
              nestedRows={organizations}
              key="rowKeyValue"
              userActions={navigateToOrder}
              userActionsKey="_id"
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 20,
    paddingHorizontal: 15,
    backgroundColor: "#fff",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#000",
  },
  newOrderButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1E88E5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  newOrderText: {
    color: "#fff",
    marginRight: 5,
    fontWeight: "bold",
    cursor: "pointer",
  },
  filterMainContainer: {
    width: "100%",
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  operationsContainer: {
    padding: 15,
    backgroundColor: "#fff",
  },
  operationCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 15,
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  operationCardContent: {
    flex: 1,
  },
  operationTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
  },
  operationStatus: {
    fontSize: 14,
    color: "#555",
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  spinnerContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
  },
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
  },
  mobileLayout: {
    flexDirection: "column",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
  },
  addButton: {
    backgroundColor: "#6495ED",
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
  },
});
